import React, { useState, useEffect } from 'react';
import { ChartPie, TrendingUpDown, ChartNoAxesColumn, Award, Ellipsis, EllipsisVertical } from 'lucide-react';
import { formatNumber, formatDifference, formatRank, formatPercentile } from '../../utils/formatting';
import { getValueColor, getRateColor, getRateDiffColor, getScoreColor } from '../../utils/coloring';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import './ReportCards.css';

const ReportCards = ({ 
  setActiveTab, 
  view,
  selectedLocation, 
  selectedCrime, 
  aggregateStats,
  compareMode,              
  rateViewMode,             
  onCompareModeChange,      
  onRateViewModeChange
}) => {
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
  const [cardMenuOpen, setCardMenuOpen] = useState({
    totals: false,
    rates: false,
    scores: false
  });

  useEffect(() => {
    const handleResize = () => {
      setIsSmallMobile(window.innerWidth <= 480);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
    
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.report-card-menu') && !event.target.closest('.report-card-menu-button')) {
        setCardMenuOpen({
          totals: false,
          rates: false,
          scores: false
        });
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isNation = selectedLocation?.info?.type === 'nation';
  const isCounty = selectedLocation?.info?.type === 'county';
  const isCity = selectedLocation?.info?.type === 'city';
  const isMetro = selectedLocation?.info?.type === 'metro';
  const isCountyOrCity = isCounty || isCity;
  const isOneCountyOrCity = selectedLocation?.data?.current?.total_counties_state === 1 || 
                            selectedLocation?.data?.current?.total_cities_state === 1 ||
                            selectedLocation?.info?.total_counties_state === 1 || 
                            selectedLocation?.info?.total_cities_state === 1;  
  const isAllCounties = selectedLocation?.info?.name === 'All Counties';
  const isAllCities = selectedLocation?.info?.name === 'All Cities';
  const locationType = selectedLocation.info?.type?.charAt(0).toUpperCase() + selectedLocation.info?.type?.slice(1) || '';
  const isLocationTypeView = view === 'locationType' || selectedLocation?.info?.name?.startsWith('All ');
  const isCountyTypeView = isLocationTypeView && selectedLocation?.info?.name?.includes('Counties');
  const isCityTypeView = isLocationTypeView && selectedLocation?.info?.name?.includes('Cities');
  const stateFromAllCounties = isCountyTypeView && selectedLocation?.info?.name?.match(/All\s+([A-Z]{2})\s+Counties/);
  const stateFromAllCities = isCityTypeView && selectedLocation?.info?.name?.match(/All\s+([A-Z]{2})\s+Cities/);
  const stateAbbr = stateFromAllCounties ? stateFromAllCounties[1] : stateFromAllCities ? stateFromAllCities[1] : '';
  const isHigherGeography = ['region', 'division', 'state', 'metro', 'county', 'city'].includes(selectedLocation?.info?.type);
  const hideDetails = isHigherGeography && isLocationTypeView;
  const usesPeerRateFields = isCounty || isCity || isMetro;
  const effectiveRateViewMode = usesPeerRateFields
    ? rateViewMode
    : { rateType: 'state', rateScope: 'national' };
  const hasSecondaryInfo = !isNation && !hideDetails;
  const displayName = getCrimeDisplayName(selectedCrime);

  let countyStateAbbr = '';
  let cityStateAbbr = '';
  if ((isCounty || isCity) && selectedLocation?.info?.name) {
    const stateName = selectedLocation.info.name.split(',')[1]?.trim();
    if (isCounty) {
      countyStateAbbr = getStateAbbreviation(stateName);
    } else if (isCity) {
      cityStateAbbr = getStateAbbreviation(stateName);
    }
  }

  const comparisonPrefix = {
    state: isCountyTypeView && stateAbbr ? stateAbbr : 
           isCityTypeView && stateAbbr ? stateAbbr :
           countyStateAbbr || cityStateAbbr || stateAbbr,
    national: 'US'
  };

  const dataHasAggregateStructure = !!(
    selectedLocation?.data?.current?.totals && 
    selectedLocation?.data?.current?.trends && 
    selectedLocation?.data?.current?.rates &&
    selectedLocation?.data?.current?.scores
  );

  let current = selectedLocation?.data?.current;
  
  if (isLocationTypeView && aggregateStats) {
    current = aggregateStats;
  }

  const validateData = () => {
    if (!selectedLocation?.data?.current && !selectedLocation?.loading) return false;
    
    if (selectedLocation?.loading) return true;
    
    if (isLocationTypeView) {
      if (!dataHasAggregateStructure && aggregateStats) {
        return true;
      }
      return dataHasAggregateStructure;
    }
    
    if (!current || Object.keys(current).length === 0) return false;
    
    return true;
  };
  
  if (!validateData()) return null;

  const hasCrimeData = [
    'all_crime_count', 'violent_crime_count', 'property_crime_count',
    'murder_count', 'rape_count', 'robbery_count', 'aggravated_assault_count',
    'burglary_count', 'larceny_theft_count', 'motor_vehicle_theft_count'
  ];
  
  const noData = hasCrimeData.every(field => current?.[field] === undefined || current?.[field] === null);
  
  if (!isLocationTypeView && noData) {
    const isTransitioning = selectedLocation?.loading === false && 
      selectedLocation?.info?.name && 
      !selectedLocation?.info?.name.startsWith('All ');
      
    if (!isTransitioning) {
      return <div className="loading">No Data Available</div>;
    }
  }

  if (isLocationTypeView && (
    current?.totals?.total === undefined || 
    current?.totals?.total === null || 
    !current?.trends || 
    !current?.rates
  )) {
    return <div className="loading">No Data Available</div>;
  }
  
  const handleMenuToggle = (cardType, e) => {
    e.stopPropagation();
    
    setCardMenuOpen(prev => ({
      totals: false,
      rates: false,
      scores: false,
      [cardType]: !prev[cardType]
    }));
  };
  
  const handleMenuClick = (cardType, mode, e) => {
    e.stopPropagation();
    
    if (compareMode === mode) {
      return;
    }
    
    onCompareModeChange(mode);
    
    setCardMenuOpen(prev => ({
      ...prev,
      [cardType]: false
    }));
  };
  
  const handleRateMenuClick = (type, scope, e) => {
    e.stopPropagation();
    
    if (rateViewMode.rateType === type && rateViewMode.rateScope === scope) {
      return;
    }
    
    onRateViewModeChange({
      rateType: type,
      rateScope: scope
    });
    
    onCompareModeChange(scope);
    
    setCardMenuOpen(prev => ({
      ...prev,
      rates: false
    }));
  };
  
  const getRateComparisonValue = (data, crime, rateType, rateScope) => {
    if (!data) return 0;
    
    if (rateType === 'county') {
      return rateScope === 'state' 
        ? data[`${crime}_all_counties_state_rate`]
        : data[`${crime}_all_counties_national_rate`];
    } else if (rateType === 'city') {
      return rateScope === 'state' 
        ? data[`${crime}_all_cities_state_rate`]
        : data[`${crime}_all_cities_national_rate`];
    } else if (rateType === 'metro') {
      return data[`${crime}_all_metros_rate`];
    } else { 
      return rateScope === 'state' 
        ? data[`${crime}_state_rate`] 
        : data[`${crime}_national_rate`];
    }
  };
  
  const getRateDifferenceValue = (data, crime, rateType, rateScope) => {
    if (!data) return 0;
    
    if (rateType === 'county') {
      return rateScope === 'state' 
        ? data[`${crime}_all_counties_state_rate_diff`]
        : data[`${crime}_all_counties_national_rate_diff`];
    } else if (rateType === 'city') {
      return rateScope === 'state' 
        ? data[`${crime}_all_cities_state_rate_diff`]
        : data[`${crime}_all_cities_national_rate_diff`];
    } else if (rateType === 'metro') {
      return data[`${crime}_all_metros_rate_diff`];
    } else { 
      return rateScope === 'state' 
        ? data[`${crime}_state_rate_diff`] 
        : data[`${crime}_national_rate_diff`];
    }
  };
  
  const getRatePercentDiffValue = (data, crime, rateType, rateScope) => {
    if (!data) return 0;
    
    if (rateType === 'county') {
      return rateScope === 'state' 
        ? data[`${crime}_all_counties_state_rate_diff_pct`]
        : data[`${crime}_all_counties_national_rate_diff_pct`];
    } else if (rateType === 'city') {
      return rateScope === 'state' 
        ? data[`${crime}_all_cities_state_rate_diff_pct`]
        : data[`${crime}_all_cities_national_rate_diff_pct`];
    } else if (rateType === 'metro') {
      return data[`${crime}_all_metros_rate_diff_pct`];
    } else { 
      return rateScope === 'state' 
      ? data[`${crime}_state_rate_diff_pct`] 
      : data[`${crime}_national_rate_diff_pct`];
    }
  };
  
  const getRateComparisonLabel = (rateType, rateScope, prefixes, crimeName) => {
    if (rateType === 'county') {
      return rateScope === 'state'
        ? `${prefixes.state} County ${crimeName} Rate`
        : `US County ${crimeName} Rate`;
    } else if (rateType === 'city') {
      return rateScope === 'state'
        ? `${prefixes.state} City ${crimeName} Rate`
        : `US City ${crimeName} Rate`;
    } else if (rateType === 'metro') {
      return `Metro ${crimeName} Rate`;
    } else { 
      return rateScope === 'state'
        ? `${prefixes.state} State ${crimeName} Rate`
        : `${(isCounty || isCity) ? 'US' : ''} National ${crimeName} Rate`;
    }
  };
  
  const getDifferenceLabel = (rateType, rateScope, prefixes) => {
    if (rateType === 'county') {
      return rateScope === 'state'
        ? `${prefixes.state} County Rate Difference`
        : `US County Rate Difference`;
    } else if (rateType === 'city') {
      return rateScope === 'state'
        ? `${prefixes.state} City Rate Difference`
        : `US City Rate Difference`;
    } else if (rateType === 'metro') {
      return `Metro Rate Difference`;
    } else { 
      return rateScope === 'state'
        ? `${prefixes.state} State Rate Difference`
        : `${(isCounty || isCity) ? 'US' : ''} National Rate Difference`;
    }
  };

  return (
    <div className={`report-cards-container ${isNation ? 'nation-view' : ''} ${!hasSecondaryInfo ? 'no-secondary-info' : ''}`}>
      <div className="report-card" onClick={() => setActiveTab('totals')}>
        <div className="report-card-content">
          <div className="report-card-icon">
            <ChartPie size={32} />
          </div>
          <div className={`report-card-text ${isCountyOrCity ? 'margin-right' : ''}`}>
            <h3>KRIME Total</h3>
            <p className="primary-value">
              {isLocationTypeView 
                ? formatNumber(current.totals.total)
                : formatNumber(current[`${selectedCrime}_count`])}
            </p>
          </div>
          {!isNation && (isCounty || isCity) && !isAllCounties && !isAllCities && !isOneCountyOrCity && 
            <div className="report-card-menu-container">
              <button 
                className={`report-card-menu-button ${cardMenuOpen.totals ? 'menu-open' : ''}`} 
                onClick={(e) => handleMenuToggle('totals', e)}
              >
                {isSmallMobile ? <EllipsisVertical size={20} /> : <Ellipsis size={20} />}
              </button>
              {cardMenuOpen.totals && (
                <div className="report-card-menu">
                  <button 
                    className={compareMode === 'state' ? 'active' : ''}
                    onClick={(e) => handleMenuClick('totals', 'state', e)}
                  >
                    {comparisonPrefix.state} {isCounty ? 'County' : 'City'}
                  </button>
                  <button 
                    className={compareMode === 'national' ? 'active' : ''}
                    onClick={(e) => handleMenuClick('totals', 'national', e)}
                  >
                    US {isCounty ? 'County' : 'City'}
                  </button>
                </div>
              )}
            </div>
          }
        </div>
        {!isNation && (
          <div className="secondary-info">
            <div className="secondary-info-item">
              <span>
                {(isCounty || isCity)
                  ? `${comparisonPrefix[compareMode]} ${locationType} ${displayName} ${isSmallMobile ? 'Avg' : 'Average'}` 
                  : `${locationType} ${displayName} ${isSmallMobile ? 'Avg' : 'Average'}`}
              </span>
              <span>
                {isLocationTypeView 
                  ? (isCountyTypeView || isCityTypeView) && compareMode === 'state'
                    ? formatNumber(current.totals.stateAverage)
                    : (isCountyTypeView || isCityTypeView) && compareMode === 'national'
                      ? formatNumber(current.totals.average || current[`${selectedCrime.toLowerCase()}_national_count_avg`])
                      : formatNumber(current.totals.average)
                  : (isCounty || isCity) && compareMode === 'state'
                    ? formatNumber(current[`${selectedCrime}_state_count_avg`])
                    : formatNumber(current[`${selectedCrime}_national_count_avg`])}
              </span>
            </div>
            <div className="secondary-info-item">
              <span>
                  {isLocationTypeView && !isCountyTypeView && !isCityTypeView
                    ? `Total ${locationType}s`
                    : (isCountyTypeView || isCityTypeView) && compareMode === 'state'
                      ? `Total ${stateAbbr} ${isCountyTypeView ? 'Counties' : 'Cities'}`
                      : (isCountyTypeView || isCityTypeView) && compareMode === 'national'
                        ? `Total US ${isCountyTypeView ? 'Counties' : 'Cities'}`
                      : (isCounty || isCity)
                        ? `${comparisonPrefix[compareMode]} ${locationType} Percentile`
                        : `${locationType} Percentile`}
                </span>
                <span>
                  {(() => {
                    if (isLocationTypeView) {
                      return (isCountyTypeView || isCityTypeView) && compareMode === 'state'
                        ? formatNumber(current.totals.stateLocations)
                        : (isCountyTypeView || isCityTypeView) && compareMode === 'national'
                          ? formatNumber(current.totals.nationalLocations)
                          : formatNumber(current.totals.locations);
                    } else {
                      return formatPercentile(
                        (isCounty || isCity) && compareMode === 'state'
                          ? current[`${selectedCrime}_state_count_percentile`]
                          : (isCounty || isCity) && compareMode === 'national'
                            ? current[`${selectedCrime}_national_count_percentile`]
                            : current[`${selectedCrime}_count_percentile`]
                      );
                    }
                  })()}
                </span>
            </div>
          </div>
        )}
      </div>

      <div className="report-card" onClick={() => setActiveTab('trends')}>
        <div className="report-card-content">
          <div className="report-card-icon">
            <TrendingUpDown size={32} />
          </div>
          <div className={`report-card-text ${isCountyOrCity ? 'margin-right' : ''}`}>
            <h3>KRIME Trend</h3>
            <p className="primary-value" style={{ 
              color: getValueColor(isLocationTypeView ? current.trends.percentChange : current[`${selectedCrime}_count_prev_diff_pct`]) 
            }}>
              {isLocationTypeView 
                ? `${formatDifference(current.trends.percentChange, true)}`
                : `${formatDifference(current[`${selectedCrime}_count_prev_diff_pct`], true)}`}
            </p>
          </div>
        </div>
        {!isNation && (
          <div className="secondary-info">
            <div className="secondary-info-item">
              <span>{`Previous ${displayName} Total`}</span>
              <span>
                {isLocationTypeView 
                  ? formatNumber(current.trends.previous)
                  : formatNumber(current[`${selectedCrime}_count_prev`])}
              </span>
            </div>
            <div className="secondary-info-item">
              <span>Change from Previous</span>
              <span style={{ 
                color: getValueColor(isLocationTypeView ? current.trends.difference : current[`${selectedCrime}_count_prev_diff`]) 
              }}>
                {isLocationTypeView 
                  ? formatDifference(current.trends.difference)
                  : formatDifference(current[`${selectedCrime}_count_prev_diff`])}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="report-card" onClick={() => setActiveTab('rates')}>
        <div className="report-card-content">
          <div className="report-card-icon">
            <ChartNoAxesColumn size={32} />
          </div>
          <div className={`report-card-text ${isCountyOrCity ? 'margin-right' : ''}`}>
            <h3>KRIME Rate</h3>
            <p className="primary-value" style={{ 
              color: isLocationTypeView ? 'black' : getRateColor(
                current[`${selectedCrime}_rate`],
                (isCounty || isCity || isMetro) ? 
                  getRateComparisonValue(current, selectedCrime.toLowerCase(), rateViewMode.rateType, rateViewMode.rateScope)
                  : current[`${selectedCrime}_national_rate`]
              ) 
            }}>
              {isLocationTypeView 
                ? formatNumber(current.rates.rate)
                : formatNumber(current[`${selectedCrime}_rate`])}
            </p>
          </div>
          {!isNation && !isLocationTypeView && (isCounty || isCity || isMetro) && !hideDetails && 
            <div className="report-card-menu-container">
              <button 
                className={`report-card-menu-button ${cardMenuOpen.rates ? 'menu-open' : ''}`} 
                onClick={(e) => handleMenuToggle('rates', e)}
              >
                {isSmallMobile ? <EllipsisVertical size={20} /> : <Ellipsis size={20} />} 
              </button>
              {cardMenuOpen.rates && (
                <div className="report-card-menu report-card-rates-menu">
                  {isMetro ? (
                    <>
                      <button 
                        className={rateViewMode.rateType === 'metro' ? 'active' : ''}
                        onClick={(e) => handleRateMenuClick('metro', 'national', e)}
                      >
                        Metro
                      </button>
                      <button 
                        className={rateViewMode.rateType === 'state' ? 'active' : ''}
                        onClick={(e) => handleRateMenuClick('state', 'national', e)}
                      >
                        National
                      </button>
                    </>
                  ) : (
                    <>
                      {!isOneCountyOrCity && (
                        <>
                          <button 
                            className={rateViewMode.rateScope === 'state' && rateViewMode.rateType === (isCounty ? 'county' : 'city') ? 'active' : ''}
                            onClick={(e) => handleRateMenuClick(isCounty ? 'county' : 'city', 'state', e)}
                          >
                            {comparisonPrefix.state} {isCounty ? 'County' : 'City'}
                          </button>
                          <button 
                            className={rateViewMode.rateScope === 'state' && rateViewMode.rateType === (isCounty ? 'all_counties' : 'all_cities') ? 'active' : ''}
                            onClick={(e) => handleRateMenuClick(isCounty ? 'all_counties' : 'all_cities', 'state', e)}
                          >
                            {comparisonPrefix.state} State
                          </button>
                        </>
                      )}
                      <button 
                        className={rateViewMode.rateScope === 'national' && rateViewMode.rateType === (isCounty ? 'county' : 'city') ? 'active' : ''}
                        onClick={(e) => handleRateMenuClick(isCounty ? 'county' : 'city', 'national', e)}
                      >
                        US {isCounty ? 'County' : 'City'}
                      </button>
                      <button 
                        className={rateViewMode.rateScope === 'national' && rateViewMode.rateType === (isCounty ? 'all_counties' : 'all_cities') ? 'active' : ''}
                        onClick={(e) => handleRateMenuClick(isCounty ? 'all_counties' : 'all_cities', 'national', e)}
                      >
                        US National
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          }
        </div>
        {!isNation && !hideDetails && (
          <div className="secondary-info">
            <div className="secondary-info-item">
              <span>
                {getRateComparisonLabel(effectiveRateViewMode.rateType, effectiveRateViewMode.rateScope, comparisonPrefix, displayName)}
              </span>
              <span>
                {isLocationTypeView 
                  ? formatNumber(current.rates.nationalRate)
                  : formatNumber(getRateComparisonValue(current, selectedCrime.toLowerCase(), effectiveRateViewMode.rateType, effectiveRateViewMode.rateScope))}
              </span>
            </div>
            <div className="secondary-info-item">
              <span>
                {getDifferenceLabel(effectiveRateViewMode.rateType, effectiveRateViewMode.rateScope, comparisonPrefix)}
              </span>
              <span>
                {(() => {
                  const diffValue = isLocationTypeView 
                    ? current.rates.difference 
                    : getRateDifferenceValue(current, selectedCrime.toLowerCase(), effectiveRateViewMode.rateType, effectiveRateViewMode.rateScope);
                  
                  const percentValue = isLocationTypeView 
                    ? current.rates.percentDiff 
                    : getRatePercentDiffValue(current, selectedCrime.toLowerCase(), effectiveRateViewMode.rateType, effectiveRateViewMode.rateScope);
                  
                  if ((diffValue === null || diffValue === undefined || diffValue === '' || diffValue === 'N/A' || isNaN(parseFloat(diffValue))) ||
                      (percentValue === null || percentValue === undefined || percentValue === '' || percentValue === 'N/A' || isNaN(parseFloat(percentValue)))) {
                    return '-';
                  }
                  
                  return (
                    <>
                      <span style={{ 
                        color: getRateDiffColor(diffValue) 
                      }}>
                        {`${formatDifference(diffValue, false, true)} `}
                      </span>
                      <span style={{ color: 'black' }}>(</span>
                      <span style={{ 
                        color: getValueColor(percentValue)
                      }}>
                        {`${formatDifference(percentValue, true)}`}
                      </span>
                      <span style={{ color: 'black' }}>)</span>
                    </>
                  );
                })()}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="report-card" onClick={() => setActiveTab('scores')}>
        <div className="report-card-content">
          <div className="report-card-icon">
            <Award size={32} />
          </div>
          <div className={`report-card-text ${isCountyOrCity ? 'margin-right' : ''}`}>
            <h3>{isCountyOrCity ? compareMode === 'state' ? `${comparisonPrefix[compareMode]} ` : 'US ' : ''}KRIME Score&trade;</h3>
            <p className="primary-value" style={{ 
              color: isLocationTypeView || isNation 
                ? 'black' 
                : getScoreColor(
                    (isCounty || isCity) && compareMode === 'state'
                      ? current.state_composite_score
                      : (isCounty || isCity) && compareMode === 'national'
                        ? current.national_composite_score
                        : current.composite_score
                  ) 
            }}>
              {isNation || hideDetails 
                ? '-' 
                : formatNumber(
                    isLocationTypeView 
                      ? current.scores.compositeScore 
                      : (isCounty || isCity) && compareMode === 'state'
                        ? current.state_composite_score
                        : (isCounty || isCity) && compareMode === 'national'
                          ? current.national_composite_score
                          : current.composite_score, 
                    true
                  )}
            </p>
          </div>
          {!isNation && !isLocationTypeView && (isCounty || isCity) && !isOneCountyOrCity && !hideDetails && 
            <div className="report-card-menu-container">
              <button 
                className={`report-card-menu-button ${cardMenuOpen.scores ? 'menu-open' : ''}`} 
                onClick={(e) => handleMenuToggle('scores', e)}
              >
                {isSmallMobile ? <EllipsisVertical size={20} /> : <Ellipsis size={20} />}
              </button>
              {cardMenuOpen.scores && (
                <div className="report-card-menu">
                  <button 
                    className={compareMode === 'state' ? 'active' : ''}
                    onClick={(e) => handleMenuClick('scores', 'state', e)}
                  >
                    {comparisonPrefix.state} {isCounty ? 'County' : 'City'}
                  </button>
                  <button 
                    className={compareMode === 'national' ? 'active' : ''}
                    onClick={(e) => handleMenuClick('scores', 'national', e)}
                  >
                    US {isCounty ? 'County' : 'City'}
                  </button>
                </div>
              )}
            </div>
          }
        </div>
        {!isNation && !hideDetails && (
          <div className="secondary-info">
            <div className="secondary-info-item">
              <span>
                {(isCounty || isCity)
                  ? `${comparisonPrefix[compareMode]} ${locationType} Rank (Composite)` 
                  : `${locationType} Rank (Composite)`
                }
              </span>
              <span>
                {(() => {
                  if (isLocationTypeView) {
                    return '-';
                  } else if ((isCounty || isCity) && compareMode === 'state') {
                    const rank = current[`state_composite_score_rank`];
                    if (!rank || rank === '' || rank === 'N/A') {
                      return '-';
                    }
                    return `${formatRank(rank)} out of ${formatNumber(isCounty ? current.total_counties_state : current.total_cities_state) || '-'}`;
                  } else if ((isCounty || isCity) && compareMode === 'national') {
                    const rank = current[`national_composite_score_rank`];
                    if (!rank || rank === '' || rank === 'N/A') {
                      return '-';
                    }
                    return `${formatRank(rank)} out of ${formatNumber(isCounty ? current.total_counties_national : current.total_cities_national) || '-'}`;
                  } else {
                    const rank = current[`composite_score_rank`];
                    if (!rank || rank === '' || rank === 'N/A') {
                      return '-';
                    }
                    return `${formatRank(rank)} out of ${formatNumber(current.total_locations) || '-'}`;
                  }
                })()}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportCards;